import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Megaphone, RefreshCw, TrendingUp, AlertTriangle, Star, GraduationCap,
  Facebook, Search as SearchIcon, Globe, ArrowUpDown, Info, Radio, Target,
  MessageCircle, MousePointerClick, Filter, Users, Building2,
} from 'lucide-react';

const TABS = [
  { value: 'sources', label: 'Lead Sources', icon: Radio },
  { value: 'campaigns', label: 'Campaigns & Ads', icon: Target },
  { value: 'funnel', label: 'Funnel', icon: Filter },
  { value: 'counselors', label: 'Counselors', icon: Users },
];
const GROUPS = [{ value: 'campaign', label: 'Campaign' }, { value: 'adset', label: 'Ad Set' }, { value: 'ad', label: 'Ad' }];
const CHANNELS = [
  { value: 'all', label: 'All', icon: Globe },
  { value: 'meta', label: 'Meta', icon: Facebook },
  { value: 'google', label: 'Google', icon: SearchIcon },
];

const SRC_ICON = {
  'Meta Lead Forms': Facebook, 'Meta Ads': Facebook, 'Click-to-WhatsApp': MessageCircle,
  'WATI / WhatsApp': MessageCircle, 'Google Ads': SearchIcon, 'Website': Globe,
  'Manual / Direct': MousePointerClick, 'Other': Radio,
};

const qColor = (s) => s >= 70 ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
  : s >= 40 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-rose-100 text-rose-700 border-rose-200';
const badColor = (r) => r >= 40 ? 'text-rose-600' : r >= 20 ? 'text-amber-600' : 'text-slate-700';

const StatCard = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <Card data-testid={testid} className="border-slate-200">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg bg-${tone}-50`}><Icon className={`h-4 w-4 text-${tone}-600`} /></span>
      </div>
      <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </CardContent>
  </Card>
);

const ACCOUNT_THEME = {
  "Patrick's": { ring: 'border-indigo-200', bar: 'bg-indigo-600', chip: 'bg-indigo-50 text-indigo-700', text: 'text-indigo-700' },
  'Gradbud': { ring: 'border-emerald-200', bar: 'bg-emerald-600', chip: 'bg-emerald-50 text-emerald-700', text: 'text-emerald-700' },
};

const AccountCompareCard = ({ data }) => {
  const rows = data?.rows || [];
  if (!rows.length) return null;
  const totalAll = rows.reduce((a, r) => a + r.total, 0);
  const lead = { total: Math.max(...rows.map((r) => r.total)), quality_score: Math.max(...rows.map((r) => r.quality_score)) };
  return (
    <Card className="border-slate-200" data-testid="mk-account-comparison">
      <CardHeader className="py-4">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
          <Building2 className="h-4 w-4 text-indigo-600" />Patrick&apos;s vs Gradbud
          <span className="ml-2 text-xs font-normal text-slate-400">at a glance · all channels combined</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {rows.map((r) => {
            const th = ACCOUNT_THEME[r.account] || ACCOUNT_THEME["Patrick's"];
            const share = totalAll ? Math.round((100 * r.total) / totalAll) : 0;
            return (
              <div key={r.account} className={`relative overflow-hidden rounded-xl border ${th.ring} bg-white p-5`} data-testid={`mk-acct-${r.account.replace(/[^a-z]/gi, '').toLowerCase()}`}>
                <div className={`absolute inset-x-0 top-0 h-1 ${th.bar}`} />
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${th.chip}`}><Building2 className={`h-4 w-4 ${th.text}`} /></span>
                    <div>
                      <div className="font-semibold text-slate-900">{r.account}</div>
                      <div className="text-xs text-slate-400">{share}% of all leads</div>
                    </div>
                  </div>
                  <Badge variant="outline" className={qColor(r.quality_score)}>Q {r.quality_score}{r.quality_score === lead.quality_score && totalAll ? ' ★' : ''}</Badge>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-3xl font-semibold tracking-tight text-slate-900">{r.total.toLocaleString()}</span>
                  <span className="text-sm text-slate-400">leads</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 py-2">
                    <div className="text-lg font-semibold text-emerald-700">{r.converted}</div>
                    <div className="text-[11px] text-slate-400">Converted · {r.conversion_rate}%</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 py-2">
                    <div className="text-lg font-semibold text-amber-700">{r.prospects}</div>
                    <div className="text-[11px] text-slate-400">Prospects · {r.prospect_rate}%</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 py-2">
                    <div className={`text-lg font-semibold ${badColor(r.bad_rate)}`}>{r.bad_rate}%</div>
                    <div className="text-[11px] text-slate-400">Bad-lead rate</div>
                  </div>
                </div>
                {r.top_source && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
                    <TrendingUp className="h-3.5 w-3.5 text-slate-400" />
                    Mostly from <span className="font-medium text-slate-700">{r.top_source}</span>
                    <span className="text-slate-400">({r.top_source_count})</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {data?.unattributed && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-400" data-testid="mk-acct-unattributed">
            <Info className="h-3.5 w-3.5" />
            {data.unattributed.total.toLocaleString()} leads couldn&apos;t be attributed to an account (no Meta/WATI account tag).
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function Marketing() {
  const [tab, setTab] = useState('sources');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [intakeId, setIntakeId] = useState('');
  const [intakeList, setIntakeList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load intake periods and default the view to the current active intake's window.
  useEffect(() => {
    apiClient.get('/marketing/filters').then((r) => {
      const items = r.data?.intakes || [];
      setIntakeList(items);
      const def = r.data?.default_intake_id;
      const act = items.find((i) => i.id === def);
      if (act && act.start_date && act.end_date) {
        setIntakeId(act.id);
        setDateFrom(act.start_date);
        setDateTo(act.end_date);
      }
    }).catch(() => {});
  }, []);

  const applyIntake = (id) => {
    setIntakeId(id);
    if (!id) { setDateFrom(''); setDateTo(''); return; }  // All time
    const it = intakeList.find((i) => i.id === id);
    if (it && it.start_date && it.end_date) { setDateFrom(it.start_date); setDateTo(it.end_date); }
  };

  // campaigns tab state
  const [groupBy, setGroupBy] = useState('campaign');
  const [channel, setChannel] = useState('all');
  const [campData, setCampData] = useState(null);
  const [sortKey, setSortKey] = useState('total');
  const [sortDir, setSortDir] = useState('desc');

  // sources state
  const [sources, setSources] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [counselors, setCounselors] = useState(null);
  const [accountCmp, setAccountCmp] = useState(null);

  // Account comparison (Patrick's vs Gradbud) — always visible, refetch on date change
  useEffect(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    apiClient.get('/marketing/account-comparison', { params: p })
      .then((r) => setAccountCmp(r.data)).catch(() => {});
  }, [dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    try {
      if (tab === 'sources') {
        const r = await apiClient.get('/marketing/lead-sources', { params: p });
        setSources(r.data);
      } else if (tab === 'funnel') {
        const r = await apiClient.get('/marketing/funnel', { params: p });
        setFunnel(r.data);
      } else if (tab === 'counselors') {
        const r = await apiClient.get('/marketing/counselor-performance', { params: p });
        setCounselors(r.data);
      } else {
        const r = await apiClient.get('/marketing/campaign-performance', { params: { ...p, group_by: groupBy, channel } });
        setCampData(r.data);
      }
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [tab, dateFrom, dateTo, groupBy, channel]);

  useEffect(() => { load(); }, [load]);

  const sortBy = (k) => { if (sortKey === k) setSortDir((d) => d === 'desc' ? 'asc' : 'desc'); else { setSortKey(k); setSortDir('desc'); } };
  const campRows = (campData?.rows || []).map((r) => ({
    ...r,
    qualified_rate: r.total ? Math.round(1000 * ((r.qualified || 0) / r.total)) / 10 : 0,
  })).sort((a, b) => {
    const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
    if (typeof av === 'string') return sortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const t = campData?.totals || {};
  const srcRows = sources?.rows || [];
  const srcTotal = srcRows.reduce((a, r) => a + r.total, 0);

  // Group lead-source rows by ACCOUNT (the part after "· ") so each account —
  // e.g. "Patrick's WATI", "Gradbud WATI", "Patrick's Meta" — shows one total
  // line with its entry-types (Click-to-WhatsApp / WATI-WhatsApp / Lead Forms) nested.
  const accountGroups = React.useMemo(() => {
    const groups = {};
    for (const r of srcRows) {
      const parts = String(r.source).split(' · ');
      const account = parts.length > 1 ? parts.slice(1).join(' · ') : parts[0];
      const entry = parts.length > 1 ? parts[0] : null;
      if (!groups[account]) {
        groups[account] = { account, entry, rows: [], total: 0, connected: 0, not_connected: 0,
          qualified: 0, non_qualified: 0, bad: 0, prospects: 0, converted: 0, _qw: 0 };
      }
      const g = groups[account];
      g.rows.push({ ...r, entry });
      g.total += r.total || 0; g.connected += r.connected || 0; g.not_connected += r.not_connected || 0;
      g.qualified += r.qualified || 0; g.non_qualified += r.non_qualified || 0;
      g.bad += (r.bad != null ? r.bad : Math.round((r.bad_rate || 0) * (r.total || 0) / 100));
      g.prospects += r.prospects || 0; g.converted += r.converted || 0;
      g._qw += (r.quality_score || 0) * (r.total || 0);
    }
    return Object.values(groups)
      .map((g) => ({ ...g,
        bad_rate: g.total ? Math.round(1000 * g.bad / g.total) / 10 : 0,
        conversion_rate: g.total ? Math.round(1000 * g.converted / g.total) / 10 : 0,
        quality_score: g.total ? Math.round(g._qw / g.total) : 0,
        single: g.rows.length === 1 && !g.rows[0].entry }))
      .sort((a, b) => b.total - a.total);
  }, [srcRows]);

  const [expandedAccounts, setExpandedAccounts] = useState({});
  const toggleAccount = (a) => setExpandedAccounts((p) => ({ ...p, [a]: !p[a] }));

  const Th = ({ label, k }) => (
    <th data-testid={`mk-th-${k}`} onClick={() => sortBy(k)} className="cursor-pointer select-none px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 hover:text-slate-800">
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className="h-3 w-3 opacity-40" /></span>
    </th>
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-8" data-testid="marketing-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-900"><Megaphone className="h-6 w-6 text-indigo-600" /><h1 className="text-2xl font-semibold tracking-tight">Sales &amp; Marketing</h1></div>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Where every lead comes from, how good it is, and which campaigns convert — one view for the marketing team to improve Meta &amp; Google ads.</p>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <span data-testid="mk-active-range" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${(dateFrom || dateTo) ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>
            {(dateFrom || dateTo) ? `Showing: ${dateFrom || '…'} → ${dateTo || '…'}` : 'Showing: All time'}
          </span>
          <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="mk-intake-select"
            value={intakeId}
            onChange={(e) => applyIntake(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          >
            <option value="">All time (custom)</option>
            {intakeList.map((i) => (
              <option key={i.id} value={i.id} disabled={!i.selectable}>
                {i.name}{i.status ? ` · ${i.status}` : ''}{!i.selectable ? ' (no dates)' : ''}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1" data-testid="mk-daterange">
            <span className="text-xs text-slate-400">From</span>
            <input type="date" data-testid="mk-date-from" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setIntakeId(''); }} className="border-0 bg-transparent text-sm text-slate-700 focus:outline-none" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" data-testid="mk-date-to" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setIntakeId(''); }} className="border-0 bg-transparent text-sm text-slate-700 focus:outline-none" />
            {(dateFrom || dateTo) && <button data-testid="mk-date-clear" onClick={() => { setDateFrom(''); setDateTo(''); setIntakeId(''); }} className="ml-1 text-xs text-indigo-600 hover:underline">clear</button>}
          </div>
          <Button data-testid="mk-refresh" variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        </div>
        </div>
      </div>

      {/* Account comparison — Patrick's vs Gradbud (always visible) */}
      {accountCmp && <AccountCompareCard data={accountCmp} />}

      {/* Tabs */}
      <div className="inline-flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1" data-testid="mk-tabs">
        {TABS.map(({ value, label, icon: Icon }) => (
          <button key={value} data-testid={`mk-tab-${value}`} onClick={() => setTab(value)}
            className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === value ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {/* SOURCES */}
      {tab === 'sources' && (
        <div className="space-y-4" data-testid="mk-sources">
          <p className="text-sm text-slate-500">Each channel tagged separately — Meta Lead Forms, Click-to-WhatsApp, Google Ads, Website, WATI/WhatsApp and Manual — with lead volume and quality.</p>
          <Card className="overflow-hidden border-slate-200"><CardContent className="p-0"><div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left" data-testid="mk-sources-table">
              <thead className="border-b border-slate-100 bg-slate-50/60"><tr>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Source</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Leads</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Connected</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Not Conn.</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Qualified</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Non-Qual</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Share</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Bad %</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Prospects</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Converted</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Quality</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
                  : accountGroups.length === 0 ? <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">No data.</td></tr>
                    : accountGroups.map((g) => {
                      const Icon = SRC_ICON[g.single ? g.rows[0].source : g.account] || Radio;
                      const open = !!expandedAccounts[g.account];
                      const hasChildren = !g.single && g.rows.length > 0;
                      return (
                      <React.Fragment key={g.account}>
                        <tr data-testid={`mk-acct-${g.account}`} className={`hover:bg-slate-50/60 ${hasChildren ? 'cursor-pointer bg-slate-50/40' : ''}`} onClick={hasChildren ? () => toggleAccount(g.account) : undefined}>
                          <td className="px-3 py-3"><div className="flex items-center gap-2">
                            {hasChildren ? <i className={`fas fa-chevron-${open ? 'down' : 'right'} text-[10px] text-slate-400 w-3`}></i> : <span className="w-3" />}
                            <Icon className="h-4 w-4 text-indigo-500" />
                            <span className="text-sm font-semibold text-slate-800">{g.single ? g.rows[0].source : g.account}</span>
                            {hasChildren && <span className="text-[11px] text-slate-400">({g.rows.length})</span>}
                          </div></td>
                          <td className="px-3 py-3 text-sm font-semibold text-slate-900">{g.total.toLocaleString()}</td>
                          <td className="px-3 py-3 text-sm text-slate-600">{g.connected}</td>
                          <td className="px-3 py-3 text-sm text-slate-600">{g.not_connected}</td>
                          <td className="px-3 py-3 text-sm font-medium text-emerald-600">{g.qualified}</td>
                          <td className="px-3 py-3 text-sm text-rose-600">{g.non_qualified}</td>
                          <td className="px-3 py-3 text-sm text-slate-500">{srcTotal ? Math.round(100 * g.total / srcTotal) : 0}%</td>
                          <td className={`px-3 py-3 text-sm font-medium ${badColor(g.bad_rate)}`}>{g.bad_rate}%</td>
                          <td className="px-3 py-3 text-sm text-slate-600">{g.prospects}</td>
                          <td className="px-3 py-3 text-sm text-slate-600">{g.converted} <span className="text-xs text-slate-400">({g.conversion_rate}%)</span></td>
                          <td className="px-3 py-3"><Badge className={`border ${qColor(g.quality_score)}`}>{g.quality_score}</Badge></td>
                        </tr>
                        {hasChildren && open && g.rows.map((r) => (
                          <tr key={r.source} data-testid={`mk-src-${r.source}`} className="bg-white hover:bg-slate-50/60">
                            <td className="px-3 py-2.5 pl-12"><span className="text-sm text-slate-600">{r.entry || r.source}</span></td>
                            <td className="px-3 py-2.5 text-sm text-slate-700">{r.total.toLocaleString()}</td>
                            <td className="px-3 py-2.5 text-sm text-slate-500">{r.connected ?? 0}</td>
                            <td className="px-3 py-2.5 text-sm text-slate-500">{r.not_connected ?? 0}</td>
                            <td className="px-3 py-2.5 text-sm text-emerald-600">{r.qualified ?? 0}</td>
                            <td className="px-3 py-2.5 text-sm text-rose-600">{r.non_qualified ?? 0}</td>
                            <td className="px-3 py-2.5 text-sm text-slate-400">{srcTotal ? Math.round(100 * r.total / srcTotal) : 0}%</td>
                            <td className={`px-3 py-2.5 text-sm ${badColor(r.bad_rate)}`}>{r.bad_rate}%</td>
                            <td className="px-3 py-2.5 text-sm text-slate-500">{r.prospects}</td>
                            <td className="px-3 py-2.5 text-sm text-slate-500">{r.converted} <span className="text-xs text-slate-400">({r.conversion_rate}%)</span></td>
                            <td className="px-3 py-2.5"><Badge className={`border ${qColor(r.quality_score)}`}>{r.quality_score}</Badge></td>
                          </tr>
                        ))}
                      </React.Fragment>); })}
              </tbody>
            </table>
          </div></CardContent></Card>
        </div>
      )}

      {/* CAMPAIGNS */}
      {tab === 'campaigns' && (
        <div className="space-y-4" data-testid="mk-campaigns">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard testid="mk-stat-total" icon={TrendingUp} tone="indigo" label="Total Leads" value={(t.total ?? 0).toLocaleString()} sub={`${campData?.rows?.length || 0} ${groupBy}s`} />
            <StatCard testid="mk-stat-bad" icon={AlertTriangle} tone="rose" label="Bad-lead rate" value={`${t.bad_rate ?? 0}%`} sub={`${(t.bad ?? 0).toLocaleString()} bad`} />
            <StatCard testid="mk-stat-prospects" icon={Star} tone="amber" label="Prospects" value={(t.prospects ?? 0).toLocaleString()} sub={`${t.prospect_rate ?? 0}%`} />
            <StatCard testid="mk-stat-converted" icon={GraduationCap} tone="emerald" label="Converted" value={(t.converted ?? 0).toLocaleString()} sub={`${t.conversion_rate ?? 0}%`} />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="mk-group-toggle">
              {GROUPS.map((g) => <button key={g.value} data-testid={`mk-group-${g.value}`} onClick={() => setGroupBy(g.value)} className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${groupBy === g.value ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{g.label}</button>)}
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="mk-channel-toggle">
              {CHANNELS.map(({ value, label, icon: Icon }) => <button key={value} data-testid={`mk-channel-${value}`} onClick={() => setChannel(value)} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${channel === value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}><Icon className="h-3.5 w-3.5" />{label}</button>)}
            </div>
          </div>
          {campData?.note && <div data-testid="mk-note" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"><Info className="mt-0.5 h-4 w-4 flex-shrink-0" /><span>{campData.note}</span></div>}
          <Card className="overflow-hidden border-slate-200"><CardContent className="p-0"><div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left" data-testid="mk-table">
              <thead className="border-b border-slate-100 bg-slate-50/60"><tr>
                <Th label={GROUPS.find((g) => g.value === groupBy)?.label} k="group" />
                <Th label="Leads" k="total" /><Th label="Active" k="active" />
                <Th label="Qualified" k="qualified" /><Th label="Non-Qual" k="non_qualified" /><Th label="Qual %" k="qualified_rate" />
                <Th label="Bad %" k="bad_rate" />
                <Th label="Prospects" k="prospects" /><Th label="Converted" k="converted" /><Th label="Conv %" k="conversion_rate" /><Th label="Quality" k="quality_score" />
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
                  : campRows.length === 0 ? <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">No data.</td></tr>
                    : campRows.map((r) => (
                      <tr key={r.group} data-testid={`mk-row-${r.group}`} className="hover:bg-slate-50/60">
                        <td className="max-w-[280px] px-3 py-3"><div className="flex items-center gap-2">{r.channel === 'meta' ? <Facebook className="h-3.5 w-3.5 flex-shrink-0 text-blue-600" /> : r.channel === 'google' ? <SearchIcon className="h-3.5 w-3.5 flex-shrink-0 text-red-500" /> : <Globe className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />}<span className="truncate text-sm font-medium text-slate-800" title={r.group}>{r.group}</span></div></td>
                        <td className="px-3 py-3 text-sm font-semibold text-slate-900">{r.total.toLocaleString()}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{r.active}</td>
                        <td className="px-3 py-3 text-sm font-medium text-emerald-600">{r.qualified ?? 0}</td>
                        <td className="px-3 py-3 text-sm text-rose-600">{r.non_qualified ?? 0}</td>
                        <td data-testid={`mk-qualrate-${r.group}`} className="px-3 py-3 text-sm font-medium text-emerald-700">{r.qualified_rate}%</td>
                        <td className={`px-3 py-3 text-sm font-medium ${badColor(r.bad_rate)}`}>{r.bad_rate}%</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{r.prospects}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{r.converted}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{r.conversion_rate}%</td>
                        <td className="px-3 py-3"><Badge className={`border ${qColor(r.quality_score)}`}>{r.quality_score}</Badge></td>
                      </tr>))}
              </tbody>
            </table>
          </div></CardContent></Card>
        </div>
      )}

      {/* FUNNEL */}
      {tab === 'funnel' && (
        <div className="space-y-4" data-testid="mk-funnel">
          <Card className="border-slate-200"><CardHeader className="py-4"><CardTitle className="text-base font-semibold text-slate-800">Conversion funnel {(dateFrom || dateTo) ? '(selected period)' : ''}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {loading ? <div className="py-8 text-center text-slate-400">Loading…</div>
                : !funnel ? <div className="py-8 text-center text-slate-400">No data.</div>
                  : (<>
                    {funnel.metrics && (
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="mk-funnel-metrics">
                        {[['Leads','leads','text-slate-900'],['Connected','connected','text-sky-600'],['Not connected','not_connected','text-slate-500'],['Qualified','qualified','text-emerald-600'],['Non-qualified','non_qualified','text-rose-600']].map(([label,key,cls]) => (
                          <div key={key} className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2" data-testid={`mk-funnel-metric-${key}`}>
                            <div className={`text-lg font-bold ${cls}`}>{(funnel.metrics[key] ?? 0).toLocaleString()}</div>
                            <div className="text-[11px] text-slate-500">{label}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {(funnel.stages || []).map((s, i) => {
                      const colors = ['bg-indigo-500', 'bg-sky-500', 'bg-amber-500', 'bg-emerald-500'];
                      return (
                        <div key={s.stage} data-testid={`mk-funnel-${s.stage}`} className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-slate-600">{s.stage}</span>
                          <div className="relative h-9 flex-1 overflow-hidden rounded-lg bg-slate-100">
                            <div className={`flex h-full items-center rounded-lg ${colors[i]} px-3 text-sm font-semibold text-white transition-all`} style={{ width: `${Math.max(s.pct, 6)}%` }}>
                              {s.count.toLocaleString()}
                            </div>
                          </div>
                          <span className="w-16 text-right text-sm text-slate-500">{s.pct}%</span>
                        </div>
                      );
                    })}
                    <div className="mt-2 flex gap-6 border-t border-slate-100 pt-3 text-sm">
                      <span className="text-slate-500">Bad leads: <span className={`font-semibold ${badColor(funnel.bad_rate)}`}>{funnel.bad?.toLocaleString()} ({funnel.bad_rate}%)</span></span>
                      <span className="text-slate-500">Lead → Admission: <span className="font-semibold text-emerald-600">{funnel.stages?.[3]?.pct || 0}%</span></span>
                    </div>
                  </>)}
            </CardContent></Card>
        </div>
      )}

      {/* COUNSELORS */}
      {tab === 'counselors' && (
        <div className="space-y-4" data-testid="mk-counselors">
          <p className="text-sm text-slate-500">Per-counselor lead outcomes {(dateFrom || dateTo) ? 'for the selected period' : '(all time)'}.</p>
          <Card className="overflow-hidden border-slate-200"><CardContent className="p-0"><div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left" data-testid="mk-counselors-table">
              <thead className="border-b border-slate-100 bg-slate-50/60"><tr>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Counselor</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Leads</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Connected</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Not Conn.</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Qualified</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Non-Qual</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Converted</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Conv %</th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">Bad %</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
                  : !(counselors?.rows || []).length ? <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No data.</td></tr>
                    : counselors.rows.map((r) => (
                      <tr key={r.counselor} data-testid={`mk-cns-${r.counselor}`} className="hover:bg-slate-50/60">
                        <td className="px-3 py-3 text-sm font-medium text-slate-800">{r.counselor}</td>
                        <td className="px-3 py-3 text-sm font-semibold text-slate-900">{r.total.toLocaleString()}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{r.connected ?? 0}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{r.not_connected ?? 0}</td>
                        <td className="px-3 py-3 text-sm font-medium text-emerald-600">{r.qualified ?? 0}</td>
                        <td className="px-3 py-3 text-sm text-rose-600">{r.non_qualified ?? 0}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{r.converted}</td>
                        <td className="px-3 py-3 text-sm font-medium text-emerald-600">{r.conversion_rate}%</td>
                        <td className={`px-3 py-3 text-sm font-medium ${badColor(r.bad_rate)}`}>{r.bad_rate}%</td>
                      </tr>))}
              </tbody>
            </table>
          </div></CardContent></Card>
        </div>
      )}
    </div>
  );
}
